import { Button, Stack } from "@mui/material";
import { useApproveFollowRequestMutation } from "Api/Mutations/Social/useApproveFollowRequestMutation";
import { useDeleteFollowRequestMutation } from "Api/Mutations/Social/useDeleteFollowRequestMutation";
import { HandleGateDialog } from "Components/Social/HandleGateDialog";
import { MutualFollowConfirmDialog } from "Components/Social/MutualFollowConfirmDialog";
import { SocialSummaryRow } from "Components/Social/SocialSummaryRow";
import { SocialUserActionsMenu } from "Components/Social/SocialUserActionsMenu";
import { useHandleGate } from "Components/Social/useHandleGate";
import { useMutualFollowConfirm } from "Components/Social/useMutualFollowConfirm";
import type { FollowRequestDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Social.People;

type Props = {
  requests: ReadonlyArray<FollowRequestDto>;
};

/**
 * The requests addressed to the caller: approve or delete each. These rows exist because SOMEONE
 * ELSE acted, so unlike the other list hosts the caller may still be handle-less here —
 * approve/delete/block go through the HandleGate. Approving someone the caller already follows
 * makes them mutual followers, which is confirmed first.
 */
export const IncomingFollowRequestsList = ({ requests }: Props) => {
  const { t } = useResource();
  const { needsHandle, isGateOpen, openGate, closeGate, guarded } =
    useHandleGate();
  const { mutate: approve, isPending: isApproving } =
    useApproveFollowRequestMutation();
  const { mutate: deleteRequest, isPending: isDeleting } =
    useDeleteFollowRequestMutation();
  const { confirmMutual, mutualConfirmDialogProps } = useMutualFollowConfirm();

  const isMutating = isApproving || isDeleting;

  const handleApprove = ({ user, isFollowedByCaller }: FollowRequestDto) => {
    if (!isFollowedByCaller) {
      approve(user.identityUserPublicID);
      return;
    }
    confirmMutual(user, t(RESOURCES.Approve), () =>
      approve(user.identityUserPublicID),
    );
  };

  return (
    <Stack gap={2}>
      {requests.map((request) => (
        <SocialSummaryRow
          key={request.user.identityUserPublicID}
          user={request.user}
          action={
            <>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={isMutating}
                onClick={guarded(() => handleApprove(request))}
              >
                {t(RESOURCES.Approve)}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                disabled={isMutating}
                onClick={guarded(() =>
                  deleteRequest(request.user.identityUserPublicID),
                )}
              >
                {t(RESOURCES.Delete)}
              </Button>
              <SocialUserActionsMenu
                identityUserPublicID={request.user.identityUserPublicID}
                handle={request.user.handle}
                displayName={request.user.displayName}
                isBlockVisible
                isDisabled={isMutating}
                needsHandle={needsHandle}
                onHandleGate={openGate}
              />
            </>
          }
        />
      ))}
      <HandleGateDialog isOpen={isGateOpen} onClose={closeGate} />
      <MutualFollowConfirmDialog {...mutualConfirmDialogProps} />
    </Stack>
  );
};
